import { type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes, useId, useState } from 'react'
import { cn } from '../../lib/utils'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  icon?: string
}

export function Input({ label, error, icon, className, id, ...props }: InputProps) {
  const autoId = useId()
  const inputId = id ?? autoId
  return (
    <div>
      {label && <label htmlFor={inputId} className="form-label">{label}</label>}
      <div className="relative">
        {icon && <i className={cn(icon, 'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted')} aria-hidden />}
        <input
          id={inputId}
          className={cn('form-control', icon && 'pl-9', error && 'border-expense/60', className)}
          aria-invalid={!!error || undefined}
          {...props}
        />
      </div>
      {error && <p className="mt-1 text-xs text-expense">{error}</p>}
    </div>
  )
}

export function PasswordInput({ label, error, className, id, ...props }: InputProps) {
  const [show, setShow] = useState(false)
  const autoId = useId()
  const inputId = id ?? autoId
  return (
    <div>
      {label && <label htmlFor={inputId} className="form-label">{label}</label>}
      <div className="relative">
        <input
          id={inputId}
          type={show ? 'text' : 'password'}
          className={cn('form-control pr-10', error && 'border-expense/60', className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-0 top-0 flex h-[38px] w-10 items-center justify-center text-muted hover:text-white"
          aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
        >
          <i className={show ? 'ri-eye-off-line' : 'ri-eye-line'} aria-hidden />
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-expense">{error}</p>}
    </div>
  )
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
}

export function Select({ label, error, children, className, id, ...props }: SelectProps) {
  const autoId = useId()
  const selectId = id ?? autoId
  return (
    <div>
      {label && <label htmlFor={selectId} className="form-label">{label}</label>}
      <select id={selectId} className={cn('form-control pr-8', className)} {...props}>
        {children}
      </select>
      {error && <p className="mt-1 text-xs text-expense">{error}</p>}
    </div>
  )
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
}

export function Textarea({ label, error, className, id, rows = 4, ...props }: TextareaProps) {
  const autoId = useId()
  const textareaId = id ?? autoId
  return (
    <div>
      {label && <label htmlFor={textareaId} className="form-label">{label}</label>}
      <textarea
        id={textareaId}
        rows={rows}
        className={cn('form-control resize-y', error && 'border-expense/60', className)}
        aria-invalid={!!error || undefined}
        {...props}
      />
      {error && <p className="mt-1 text-xs text-expense">{error}</p>}
    </div>
  )
}

export function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <label className={cn('inline-flex items-center gap-2.5', disabled ? 'opacity-60' : 'cursor-pointer')}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label || undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative h-[18px] w-8 flex-shrink-0 rounded-full border transition-colors',
          checked ? 'border-primary bg-primary' : 'border-border bg-surface3',
        )}
      >
        <span
          className={cn(
            'absolute top-1/2 left-0.5 h-3 w-3 -translate-y-1/2 rounded-full transition-transform',
            checked ? 'translate-x-[14px] bg-bg' : 'bg-muted',
          )}
        />
      </button>
      {label && <span className="text-13 text-white">{label}</span>}
    </label>
  )
}
